import React, { useContext, useEffect, useState } from 'react';
import Layout from '../components/Layout';
import AddIcon from '@mui/icons-material/Add';
import styles from "./css/Projects.module.css";
import PageHeader from './PageHeader';
import {
  Box,
  Button,
  Drawer,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Popover,
  TablePagination,
  FormHelperText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DatePicker from 'react-datepicker';
import CustomerService from '../services/CustomerService';
import ProjectService from '../services/ProjectService';
import { useNavigate, useLocation } from 'react-router-dom';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ConfirmModal from './modal/ConfirmModal';
import { useToast } from '../contexts/ToastContext';
import { AuthContext } from "../contexts/AuthContext";
import Roles from "../config/roles";
import { useTranslation } from 'react-i18next';

const EMPTY_PROJECT = {
  customer: '',
  customerContactId: '',
  name: '',
  description: '',
  pricePerHour: '',
  contractedValue: '',
  budget: '',
  status: '',
  startDate: '',
  endDate: '',
};

const STATUS_OPTIONS = [
  { code: 'PENDING',   color: '#F4C067' },
  { code: 'ONGOING',   color: '#4A90E2' },
  { code: 'COMPLETED', color: '#7DCFB6' },
  { code: 'CANCELED',  color: '#E07A5F' },
];

const statusColor = (code) => STATUS_OPTIONS.find(o => o.code === code)?.color || '#94a3b8';

const ProjectsPage = () => {
  const { t } = useTranslation();
  const { role } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  const agentFilters = location.state?.agentFilters || {};

  const [loadingProjects, setLoadingProjects] = useState(false);
  const [totalNumberOfProjects, setTotalNumberOfProjects] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState(agentFilters.customerName || '');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [customerContact, setCustomerContact] = useState(null);
  const [projectData, setProjectData] = useState(EMPTY_PROJECT);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const [anchorProjectEl, setAnchorProjectEl] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isConfirmDeleteProjectOpen, setConfirmDeleteProjectOpen] = useState(false);
  const [isDeletingProject, setIsDeletingProject] = useState(false);

  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const fetchCustomers = async () => {
    try {
      const data = await CustomerService.getCustomers();
      setCustomers(data.content || []);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchCustomerContacts = async () => {
    if (!customer?.id) return;
    try {
      const data = await CustomerService.getCustomer(customer.id);
      setCustomerContacts(data.customerContact || []);
    } catch (e) {
      console.error('Failed to fetch customer contacts:', e);
    }
  };

  const fetchProjects = async () => {
    setLoadingProjects(true);
    try {
      const data = await ProjectService.getProjects(page, rowsPerPage);
      setProjects(data.content || []);
      setTotalNumberOfProjects(data.totalElements ?? 0);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  useEffect(() => {
    if (location.state?.agentFilters) {
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchCustomers();
    if (agentFilters.customerName) {
      ProjectService.searchProjects({ searchText: agentFilters.customerName })
        .then(response => {
          setProjects(response.data.content || []);
          setTotalNumberOfProjects(response.data.totalElements ?? 0);
        })
        .catch(() => fetchProjects());
    } else {
      fetchProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (customer) fetchCustomerContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  // Drawer
  const handleOpenDrawer = () => setDrawerOpen(true);

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setProjectData(EMPTY_PROJECT);
    setCustomer(null);
    setCustomerContact(null);
    setCustomerContacts([]);
    setValidationErrors({});
  };

  // Row actions
  const handleViewProject = (project) => navigate(`/project-info/${project.id}`);

  const handleMoreProjectClick = (event, project) => {
    setAnchorProjectEl(event.currentTarget);
    setSelectedProject(project);
  };
  const handleClosePopover = () => setAnchorProjectEl(null);

  const handleDeleteProject = (project) => {
    setSelectedProject(project);
    setConfirmDeleteProjectOpen(true);
  };
  const handleConfirmDeleteProject = async () => {
    setIsDeletingProject(true);
    try {
      await ProjectService.deleteProject(selectedProject.id);
      setConfirmDeleteProjectOpen(false);
      setProjects(prev => prev.filter(p => p.id !== selectedProject.id));
      showToast(t('projects.messages.deleted'), 'success');
    } catch (error) {
      console.error(error);
      setIsDeletingProject(false);
    }
  };

  // Search
  const handleSearch = async () => {
    try {
      const response = await ProjectService.searchProjects({ searchText: filter });
      setProjects(response.data.content || []);
      setTotalNumberOfProjects(response.data.totalElements ?? 0);
    } catch (error) {
      console.error('Failed to search projects:', error);
    }
  };

  // Validation
  const validateInputs = () => {
    const errors = {};
    const letterPattern = /^[A-Za-zÀ-ÿ0-9\s.,()\-_/&+%#!?:;'"€$]*$/;
    const isEmpty = (v) => !v || String(v).trim() === '';

    if (isEmpty(projectData.customer))    errors.customer    = t('projects.validation.customerRequired');
    if (isEmpty(projectData.name))        errors.name        = t('projects.validation.nameRequired');
    else if (!letterPattern.test(projectData.name)) errors.name = t('projects.validation.nameLettersOnly');
    if (isEmpty(projectData.description)) errors.description = t('projects.validation.descriptionRequired');
    else if (!letterPattern.test(projectData.description)) errors.description = t('projects.validation.descriptionLettersOnly');
    if (isEmpty(projectData.status))      errors.status      = t('projects.validation.statusRequired');
    if (isEmpty(projectData.startDate))   errors.startDate   = t('projects.validation.startDateRequired');
    if (isEmpty(projectData.endDate))     errors.endDate     = t('projects.validation.endDateRequired');

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateProject = async () => {
    if (!validateInputs()) {
      showToast(t('projects.messages.validationFailed'), 'warning');
      return;
    }
    setIsSaving(true);
    try {
      const response = await ProjectService.createProject(projectData);
      if (response) {
        setProjects(prev => [response, ...prev]);
        handleCloseDrawer();
        showToast(t('projects.messages.created'), 'success');
      } else {
        showToast(t('projects.messages.createError'), 'error');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      showToast(t('projects.messages.createError'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <PageHeader
          title={t('projects.title')}
          action={role === Roles.ADMIN && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDrawer}
              sx={{ backgroundColor: '#427898', borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
            >
              {t('projects.add')}
            </Button>
          )}
        />

        {/* Search toolbar */}
        <div className={styles.toolbar}>
          <div className={styles.searchRow}>
            <TextField
              label={t('projects.search')}
              size="small"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              sx={{ color: '#427898', borderColor: '#427898' }}
            >
              {t('projects.search')}
            </Button>
          </div>
        </div>

        <TableContainer
          component={Paper}
          className={styles.tableContainer}
          sx={{
            borderRadius: '14px',
            border: '1px solid #e6ecf1',
            boxShadow: '0 1px 2px rgba(16,42,66,0.04), 0 12px 28px -18px rgba(30,58,82,0.18)',
          }}
        >
          <Table
            sx={{
              minWidth: 600,
              borderCollapse: 'collapse',
              '& .MuiTableCell-root': { color: '#334155', p: { xs: 1, sm: 1.5 } },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell><strong>{t('projects.name')}</strong></TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <strong>{t('projects.customer')}</strong>
                </TableCell>
                <TableCell><strong>{t('projects.status')}</strong></TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <strong>{t('projects.deadline')}</strong>
                </TableCell>
                <TableCell align="right"><strong>{t('projects.actions')}</strong></TableCell>
              </TableRow>
            </TableHead>

            {loadingProjects ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Box display="flex" justifyContent="center" alignItems="center" height="10vh">
                      <CircularProgress size={40} sx={{ color: '#427898' }} />
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {projects.map((project) => (
                  <TableRow
                    key={project.id}
                    onClick={() => handleViewProject(project)}
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#eef5fb' } }}
                  >
                    <TableCell sx={{ fontWeight: 600, color: '#1E293B' }}>{project.name}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, color: '#64748b' }}>
                      {project.customer?.name || '—'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.75,
                        px: 1, py: 0.375, borderRadius: 999,
                        bgcolor: `${statusColor(project.status)}1F`,
                      }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: statusColor(project.status), flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
                          {t(`projects.statuses.${project.status || 'PENDING'}`)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, color: '#64748b' }}>
                      {project.endDate ? new Date(project.endDate).toLocaleDateString('de-CH') : t('projects.na')}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton onClick={(e) => handleMoreProjectClick(e, project)}>
                        <MoreHorizIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalNumberOfProjects}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ borderTop: '1px solid #f1f5f9' }}
          />

          <Popover
            open={Boolean(anchorProjectEl)}
            anchorEl={anchorProjectEl}
            onClose={handleClosePopover}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <MenuItem onClick={() => { handleViewProject(selectedProject); handleClosePopover(); }}>
              {t('projects.view')}
            </MenuItem>
            {role === Roles.ADMIN && (
              <MenuItem
                onClick={() => { handleDeleteProject(selectedProject); handleClosePopover(); }}
                sx={{ color: 'red' }}
              >
                {t('projects.delete')}
              </MenuItem>
            )}
          </Popover>
        </TableContainer>
      </Box>

      {/* ── Create Project Drawer ── */}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 580 }, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 32px rgba(15,23,42,0.12)' } }}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.08)' } } }}
      >
        <Box sx={{ height: 3, bgcolor: '#427898', flexShrink: 0 }} />

        {/* Header */}
        <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid #EEF2F6', flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography fontWeight={700} fontSize={16} color="#0F172A">
              {t('projects.createTitle')}
            </Typography>
            <Typography fontSize={12} color="#94A3B8" mt={0.3}>
              {t('projects.info')} · {t('projects.pricing')} · {t('projects.schedule')}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleCloseDrawer} sx={{ color: '#94A3B8', mt: -0.25 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 0.5 }}>

          {/* Section 01 — Project Info */}
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#427898', letterSpacing: 1.5, textTransform: 'uppercase', mb: 1.5 }}>
            01 — {t('projects.info')}
          </Typography>

          <Autocomplete
            value={customer}
            onChange={(_e, newValue) => {
              setCustomer(newValue);
              setProjectData(prev => ({ ...prev, customer: newValue ? newValue.id : '' }));
              setCustomerContact(null);
              setCustomerContacts([]);
            }}
            options={customers}
            getOptionLabel={(o) => o.name || ''}
            renderInput={(params) => (
              <TextField
                {...params} label={t('projects.customer')} size="small" fullWidth
                error={!!validationErrors.customer}
                helperText={validationErrors.customer || ' '}
              />
            )}
          />
          <Autocomplete
            value={customerContact}
            onChange={(_e, newValue) => {
              setCustomerContact(newValue);
              setProjectData(prev => ({ ...prev, customerContactId: newValue ? newValue.id : '' }));
            }}
            options={customerContacts}
            getOptionLabel={(o) => o ? `${o.firstName} ${o.lastName}` : ''}
            disabled={!customer}
            renderInput={(params) => (
              <TextField
                {...params} label={t('projects.customerContact')} size="small" fullWidth
                helperText=" "
              />
            )}
          />
          <TextField
            fullWidth size="small" label={t('projects.projectName')}
            value={projectData.name}
            onChange={(e) => setProjectData(prev => ({ ...prev, name: e.target.value }))}
            error={!!validationErrors.name}
            helperText={validationErrors.name || ' '}
          />
          <TextField
            fullWidth size="small" label={t('projects.projectDescription')}
            multiline rows={3}
            value={projectData.description}
            onChange={(e) => setProjectData(prev => ({ ...prev, description: e.target.value }))}
            error={!!validationErrors.description}
            helperText={validationErrors.description || ' '}
          />

          <Divider sx={{ my: 2 }} />

          {/* Section 02 — Pricing */}
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#427898', letterSpacing: 1.5, textTransform: 'uppercase', mb: 1.5 }}>
            02 — {t('projects.pricing')}
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              fullWidth size="small" label={t('projects.pricePerHourCHF')}
              type="number"
              value={projectData.pricePerHour}
              onChange={(e) => setProjectData(prev => ({ ...prev, pricePerHour: e.target.value }))}
              error={!!validationErrors.pricePerHour}
              helperText={validationErrors.pricePerHour || ' '}
            />
            <TextField
              fullWidth size="small" label={t('projects.contractedValueCHF')}
              type="number"
              value={projectData.contractedValue}
              onChange={(e) => setProjectData(prev => ({ ...prev, contractedValue: e.target.value }))}
              helperText={t('projects.contractedValueHint') || ' '}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Section 03 — Schedule */}
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#427898', letterSpacing: 1.5, textTransform: 'uppercase', mb: 1.5 }}>
            03 — {t('projects.schedule')}
          </Typography>

          <FormControl fullWidth size="small" error={!!validationErrors.status}>
            <InputLabel>{t('projects.selectStatus')}</InputLabel>
            <Select
              value={projectData.status}
              label={t('projects.selectStatus')}
              onChange={(e) => setProjectData(prev => ({ ...prev, status: e.target.value }))}
            >
              {STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.code} value={o.code}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: o.color }} />
                    {t(`projects.statuses.${o.code}`)}
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {validationErrors.status
              ? <FormHelperText>{validationErrors.status}</FormHelperText>
              : <FormHelperText> </FormHelperText>}
          </FormControl>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 0.5 }}>
            <DatePicker
              selected={projectData.startDate}
              onChange={(date) => setProjectData(prev => ({ ...prev, startDate: date }))}
              dateFormat="dd-MM-yyyy"
              customInput={
                <TextField
                  label={t('projects.startDate')} size="small" fullWidth
                  error={!!validationErrors.startDate}
                  helperText={validationErrors.startDate || ' '}
                />
              }
            />
            <DatePicker
              selected={projectData.endDate}
              onChange={(date) => setProjectData(prev => ({ ...prev, endDate: date }))}
              dateFormat="dd-MM-yyyy"
              customInput={
                <TextField
                  label={t('projects.endDate')} size="small" fullWidth
                  error={!!validationErrors.endDate}
                  helperText={validationErrors.endDate || ' '}
                />
              }
            />
          </Box>

        </Box>

        {/* Footer */}
        <Box sx={{ px: 3, py: 2.5, flexShrink: 0, borderTop: '1px solid #EEF2F6', bgcolor: '#FAFBFC', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button onClick={handleCloseDrawer} sx={{ textTransform: 'none', color: '#64748b', fontWeight: 500 }}>
            {t('projects.dismiss')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateProject}
            disabled={isSaving}
            sx={{ bgcolor: '#427898', textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none', px: 2.5, '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}
          >
            {t('projects.create')}
          </Button>
        </Box>
      </Drawer>

      <ConfirmModal
        open={isConfirmDeleteProjectOpen}
        onClose={() => !isDeletingProject && setConfirmDeleteProjectOpen(false)}
        onConfirm={handleConfirmDeleteProject}
        question={`${t('projects.deleteQuestion')} "${selectedProject?.name}"!`}
        isLoading={isDeletingProject}
      />
    </Layout>
  );
};

export default ProjectsPage;
